// ============================================================================
// Pig daily report: the SOWS label rule, in one place
// ============================================================================
//
// A SOWS pig daily used to be ONE row labelled 'SOWS'. Since the sow-groups
// change (owner request, 2026-11-04) the daily form submits one row per sow
// group, labelled 'SOWS · Group 1' / '· Group 2' / '· Group 3'.
//
// Why three labels and not three 'SOWS' rows: pig_dailys carries a unique
// index on (date, batch_label) (migration 084, still required by 234), so
// three rows labelled 'SOWS' on one day would be refused. Distinct labels need
// no migration.
//
// Every reader that used to compare a daily's batch_label with 'SOWS' must go
// through isSowsDailyLabel so the three group rows count together AND old
// 'SOWS' rows still count. Do not hand-copy the comparison.
// ============================================================================

export const SOW_GROUP_NUMBERS = Object.freeze([1, 2, 3] as const);
export type SowGroupNumber = (typeof SOW_GROUP_NUMBERS)[number];

/** The webform_config group-list value the form shows for the sows. */
export const SOWS_GROUP_VALUE = 'SOWS';

const SOW_GROUP_LABEL_RE = /^sows\s*·\s*group\s*([123])$/;

function normLabel(label: unknown): string {
  return String(label ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** 'SOWS · Group n': the batch_label a sow-group daily row is saved under. */
export function sowGroupLabel(n: SowGroupNumber): string {
  return `SOWS · Group ${n}`;
}

/** 'sows-group-n': the batch_id a sow-group daily row is saved under. */
export function sowGroupBatchId(n: SowGroupNumber): string {
  return `sows-group-${n}`;
}

/** The group number a sow-group label names, or null for any other label (including legacy 'SOWS'). */
export function sowGroupOf(label: unknown): SowGroupNumber | null {
  const m = SOW_GROUP_LABEL_RE.exec(normLabel(label));
  return m ? (Number(m[1]) as SowGroupNumber) : null;
}

/** True for the legacy 'SOWS' label and for each of the three sow-group labels, in any case or spacing. */
export function isSowsDailyLabel(label: unknown): boolean {
  const n = normLabel(label);
  return n === 'sows' || sowGroupOf(n) !== null;
}

/**
 * Every batch_label a sow group's history can sit under: its own label and the
 * legacy combined 'SOWS' label (reports filed before the split).
 */
export function sowGroupHistoryLabels(n: SowGroupNumber): string[] {
  return [sowGroupLabel(n), SOWS_GROUP_VALUE];
}

type BreederLike = {
  id?: unknown;
  tag?: unknown;
  sex?: unknown;
  group?: unknown;
  archived?: unknown;
  [field: string]: unknown;
};

export type SowTagsByGroup = Record<SowGroupNumber, string[]>;

/**
 * Active sow tags per sow group, with the Breeding Pigs page's rules: active is
 * !archived and sex Sow or Gilt; the tag reads '#' + (tag || id); the group is
 * the breeder's group '1' | '2' | '3'. Sows in no group (or any other group
 * value) are not listed under a group. Tags are sorted numerically.
 */
export function activeSowTagsByGroup(breeders: readonly BreederLike[] | null | undefined): SowTagsByGroup {
  const out: SowTagsByGroup = {1: [], 2: [], 3: []};
  for (const b of breeders || []) {
    if (!b || b.archived) continue;
    if (b.sex !== 'Sow' && b.sex !== 'Gilt') continue;
    const g = String(b.group ?? '').trim();
    if (g !== '1' && g !== '2' && g !== '3') continue;
    const tag = String(b.tag || b.id || '').trim();
    if (!tag) continue;
    out[Number(g) as SowGroupNumber].push('#' + tag);
  }
  for (const n of SOW_GROUP_NUMBERS) out[n].sort((a, b) => a.localeCompare(b, undefined, {numeric: true}));
  return out;
}

/**
 * Drops legacy daily-report 'SOWS' rows on any day that already has sow-group rows
 * ('SOWS · Group 1/2/3'), preventing double-counting of feed totals.
 * Add Feed rows (source === 'add_feed_webform') are preserved.
 */
export function deduplicateSowFeedRows<T extends {date?: unknown; batch_label?: unknown; source?: unknown}>(
  rows: readonly T[] | null | undefined,
): T[] {
  const list = rows || [];
  const datesWithGroups = new Set<string>();
  for (const r of list) {
    if (!r) continue;
    if (sowGroupOf(r.batch_label) !== null) {
      const d = String(r.date ?? '').slice(0, 10);
      if (d) datesWithGroups.add(d);
    }
  }
  return list.filter((r) => {
    if (!r) return false;
    // Keep every Add Feed row (source === 'add_feed_webform')
    if (r.source === 'add_feed_webform') return true;
    const isLegacyDaily = isSowsDailyLabel(r.batch_label) && sowGroupOf(r.batch_label) === null;
    if (isLegacyDaily) {
      const d = String(r.date ?? '').slice(0, 10);
      if (datesWithGroups.has(d)) return false;
    }
    return true;
  });
}

type SowDailyLike = {date?: unknown; batch_label?: unknown; pig_count?: unknown; [field: string]: unknown};

function countOf(v: unknown): number {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The latest sow head count from pig dailies on or before `asOf` (inclusive;
 * omit for no bound). Only rows with a positive count are evidence, as before
 * the split.
 *
 * - Since the split, each sow group carries forward its latest positive count
 *   on or before `asOf`. The total is the SUM of each group's most recent count,
 *   dated as of the most recent group date.
 * - A legacy combined 'SOWS' row still counts on its own, and wins when it is
 *   newer than every sow-group date.
 */
export function latestSowsDailyCount(
  rows: readonly SowDailyLike[] | null | undefined,
  asOf?: string | null,
): {count: number; date: string} | null {
  let groupDate = '';
  let legacy: {count: number; date: string} | null = null;
  const sorted = [...(rows || [])]
    .filter((d) => d && isSowsDailyLabel(d.batch_label) && countOf(d.pig_count) > 0)
    .map((d) => ({d, date: String(d.date ?? '').slice(0, 10)}))
    .filter(({date}) => date && (!asOf || date <= asOf))
    .sort((a, b) => a.date.localeCompare(b.date));
  for (const {d, date} of sorted) {
    if (sowGroupOf(d.batch_label) === null) legacy = {count: countOf(d.pig_count), date};
    else if (date > groupDate) groupDate = date;
  }
  if (groupDate && (!legacy || legacy.date <= groupDate)) {
    const perGroup = new Map<SowGroupNumber, number>();
    for (const {d} of sorted) {
      const g = sowGroupOf(d.batch_label);
      if (g !== null) perGroup.set(g, countOf(d.pig_count));
    }
    let count = 0;
    for (const v of perGroup.values()) count += v;
    return {count, date: groupDate};
  }
  return legacy;
}
